// Configuración simulada de EFICIENCIA ENERGÉTICA de la demo. Editar este
// fichero cambia el "guion" (tarifas, potencias, horarios, umbrales) sin tocar
// código. Todo lo que se calcula aquí DERIVA de la ocupación del simulador de
// aforo (aforoConfig.ts / AforoSimulator.ts): la gente es la que mueve el CO₂,
// la temperatura y el consumo.
//
// Las cifras de referencia (tarifa y factor de emisión) son las que traen los
// informes que nos pasaron (coliving_dashboard.html): 0,165 €/kWh y
// 0,139 kg CO₂e/kWh.

import { AforoAreaId, AforoZoneType } from "../Aforo/aforoConfig";

// ---------------------------------------------------------------------------
// Vistas del módulo: lo que se puede pintar sobre el plano.
// ---------------------------------------------------------------------------

// Vistas del módulo. Las cuatro primeras son mapas de calor sobre la planta
// (magnitudes derivadas de la ocupación); el resto colorean ELEMENTOS del
// modelo (luminarias, puestos, cargadores, caldera, placas) o, en la
// cubierta, el sombreado de incidencia solar.
export type EnergyView =
    | "co2"
    | "temperatura"
    | "consumo"
    | "desperdicio"
    | "luminarias"
    | "fuerza"
    | "cargadores"
    | "caldera"
    | "placas"
    | "incidencia"
    | "mantenimiento"
    | "agua";

/** Vistas que se pintan como mapa de calor sobre los puntos de aforo de la planta. */
export const ENERGY_MAP_VIEWS: EnergyView[] = ["co2", "temperatura", "consumo", "desperdicio"];

// Tipos de sensor del sombreador (uno por vista de mapa). Los puntos de aforo
// de cada planta atienden también a estos tipos, con su propia rampa.
export const ENERGY_SENSOR_TYPES: string[] = ENERGY_MAP_VIEWS;

export const isEnergySensorType = (type: string): type is EnergyView =>
    (ENERGY_MAP_VIEWS as string[]).includes(type);

/** Ámbito del desplegable: una planta del aforo o la cubierta fotovoltaica. */
export type EnergyScope = AforoAreaId | "cubierta";
export const ENERGY_CUBIERTA: EnergyScope = "cubierta";

export interface EnergyViewInfo {
    id: EnergyView;
    nombre: string;
    // Nombre corto para la pestaña del panel.
    corto: string;
    unidad: string;
    // Cómo se pinta: mapa de calor de la planta, elementos del modelo
    // teñidos, o el sombreado solar de la cubierta.
    tipo: "mapa" | "elementos" | "sombreado";
    // Vistas "sombreado": grupo del sombreador, tipo de sensor y radio (incidencia solar, tuberías).
    sombreado?: { grupo: string; sensor: string; radio: number };
    // Rampa del sombreador (frío → caliente) y leyenda a juego. En las vistas
    // de elementos la leyenda es discreta (estados) y la rampa no se usa.
    colores: number[];
    leyenda: Array<{ ruleName: string; ruleColor: string }>;
    // Rango de la magnitud que se mapea a 0..1 en el sombreador.
    min: number;
    max: number;
    descripcion: string;
}

// ---------------------------------------------------------------------------
// Elementos del modelo que colorean las vistas de instalaciones: prefijos de
// búsqueda por nombre (Alt+clic en modo AFORO imprime el nombre de una pieza).
// ---------------------------------------------------------------------------
export const ENERGY_ELEMENTOS = {
    // Luminarias por planta.
    luminarias: { primera: "pprimera_luz", baja: "--pbaja_luz", parking: "parking_luz" } as {
        [areaId: string]: string;
    },
    // Puestos de trabajo (fuerza) por planta: solo donde hay escritorios.
    // En el GLB cada mesa es un elemento propio ("--PPrimera_Mesa_001").
    puestos: { primera: "--pprimera_mesa" } as { [areaId: string]: string },
    cargadores: "--parking_cargelec",
    caldera: "--parking_caldera",
    placas: "placa_solar",
    // Zona del aforo cuyas plazas alimentan los cargadores.
    zonaCargadores: "parkingElectricos",
    // Piezas que tapan la caldera en la vista cenital: se vuelven translúcidas en su vista.
    calderaTransparentar: ["--Parking_Estructura", "--Parking_Puerta_001"],
};

/** Colores de los estados de los elementos (a juego con las leyendas de abajo). */
export const ENERGY_ESTADO_COLORES = {
    luzGente: "#FFD500",
    luzSinNadie: "#E60026",
    luzApagada: "#5B6B7D",
    puestoOcupado: "#FF8000",
    puestoEspera: "#1FB4D8",
    cargando: "#FF8000",
    cargadorLibre: "#A9B8C6",
    calderaApagada: "#5B6B7D",
    calderaBaja: "#39C66B",
    calderaMedia: "#FFD500",
    calderaAlta: "#E60026",
    placaNoche: "#5B6B7D",
    placaBaja: "#1FB4D8",
    placaMedia: "#FFD500",
    placaAlta: "#FF8000",
    mantBien: "#008F39",
    mantFiltraciones: "#4A69FE",
    mantDeslaminado: "#AD37FF",
    mantHotspots: "#FF3737",
};

/** Umbrales de la caldera (kW eléctricos de clima del edificio) y de las placas (W por placa). */
export const ENERGY_UMBRALES = {
    calderaBaja: 10,
    calderaMedia: 25,
    placaBaja: 150,
    placaMedia: 300,
};

/** Estados de mantenimiento fotovoltaico con su peso (determinista por placa y mes). */
export const ENERGY_MANTENIMIENTO = [
    { id: "bien", nombre: "Buen estado", color: ENERGY_ESTADO_COLORES.mantBien, peso: 0.78 },
    {
        id: "filtraciones",
        nombre: "Filtraciones",
        color: ENERGY_ESTADO_COLORES.mantFiltraciones,
        peso: 0.08,
    },
    {
        id: "deslaminado",
        nombre: "Deslaminado",
        color: ENERGY_ESTADO_COLORES.mantDeslaminado,
        peso: 0.06,
    },
    { id: "hotspots", nombre: "Hotspots", color: ENERGY_ESTADO_COLORES.mantHotspots, peso: 0.08 },
];

/** Vistas disponibles en cada ámbito (la fuerza solo donde hay puestos). */
export const viewsForScope = (scope: EnergyScope): EnergyView[] => {
    if (scope === "cubierta") return ["placas", "incidencia", "mantenimiento"];
    const views: EnergyView[] = [...ENERGY_MAP_VIEWS, "luminarias"];
    if (ENERGY_ELEMENTOS.puestos[scope]) views.push("fuerza");
    if (scope === "parking") views.push("cargadores", "caldera");
    if (scope === "primera") views.push("agua");
    return views;
};

export const ENERGY_VIEW_INFO: { [view in EnergyView]: EnergyViewInfo } = {
    luminarias: {
        id: "luminarias",
        nombre: "Luminarias",
        corto: "Luces",
        unidad: "kW",
        tipo: "elementos",
        colores: [],
        leyenda: [
            { ruleName: "Encendida con gente", ruleColor: ENERGY_ESTADO_COLORES.luzGente },
            { ruleName: "Encendida sin nadie", ruleColor: ENERGY_ESTADO_COLORES.luzSinNadie },
            { ruleName: "Apagada", ruleColor: ENERGY_ESTADO_COLORES.luzApagada },
        ],
        min: 0,
        max: 1,
        descripcion:
            "Cada luminaria de la planta según su horario y la gente de su zona: encendida con gente, encendida sin nadie (desperdicio) o apagada.",
    },
    fuerza: {
        id: "fuerza",
        nombre: "Fuerza",
        corto: "Puestos",
        unidad: "W",
        tipo: "elementos",
        colores: [],
        leyenda: [
            { ruleName: "Puesto ocupado · 122 W", ruleColor: ENERGY_ESTADO_COLORES.puestoOcupado },
            { ruleName: "En espera · 12 W", ruleColor: ENERGY_ESTADO_COLORES.puestoEspera },
        ],
        min: 0,
        max: 1,
        descripcion:
            "Consumo de fuerza de los puestos de trabajo: el equipo de cada puesto ocupado más el consumo en espera de los libres.",
    },
    cargadores: {
        id: "cargadores",
        nombre: "Cargadores",
        corto: "Cargadores",
        unidad: "kW",
        tipo: "elementos",
        colores: [],
        leyenda: [
            { ruleName: "Cargando · 5,2 kW", ruleColor: ENERGY_ESTADO_COLORES.cargando },
            { ruleName: "Libre", ruleColor: ENERGY_ESTADO_COLORES.cargadorLibre },
        ],
        min: 0,
        max: 1,
        descripcion:
            "Cargadores de las plazas eléctricas: cargan mientras su plaza está ocupada (7,4 kW al 70 %).",
    },
    caldera: {
        id: "caldera",
        nombre: "Caldera",
        corto: "Caldera",
        unidad: "kW",
        tipo: "elementos",
        colores: [],
        leyenda: [
            { ruleName: "Apagada", ruleColor: ENERGY_ESTADO_COLORES.calderaApagada },
            { ruleName: "Carga baja (< 10 kW)", ruleColor: ENERGY_ESTADO_COLORES.calderaBaja },
            { ruleName: "Carga media (10–25 kW)", ruleColor: ENERGY_ESTADO_COLORES.calderaMedia },
            { ruleName: "Carga alta (> 25 kW)", ruleColor: ENERGY_ESTADO_COLORES.calderaAlta },
        ],
        min: 0,
        max: 1,
        descripcion:
            "Central de climatización del edificio: su carga es la suma del clima de todas las zonas, que sube con la gente y con el frío o el calor exterior.",
    },
    placas: {
        id: "placas",
        nombre: "Potencia generada",
        corto: "Placas",
        unidad: "W",
        tipo: "elementos",
        colores: [],
        leyenda: [
            { ruleName: "Sin sol", ruleColor: ENERGY_ESTADO_COLORES.placaNoche },
            { ruleName: "Menos de 150 W", ruleColor: ENERGY_ESTADO_COLORES.placaBaja },
            { ruleName: "150–300 W", ruleColor: ENERGY_ESTADO_COLORES.placaMedia },
            { ruleName: "Más de 300 W", ruleColor: ENERGY_ESTADO_COLORES.placaAlta },
        ],
        min: 0,
        max: 1,
        descripcion:
            "Potencia que genera cada placa a la hora de la foto: campana solar del mes, nubosidad del día y rendimiento de cada placa.",
    },
    incidencia: {
        id: "incidencia",
        nombre: "Incidencia solar",
        corto: "Incidencia",
        unidad: "%",
        tipo: "sombreado",
        sombreado: { grupo: "Solar Panels", sensor: "heat", radio: 70 },
        colores: [],
        leyenda: [
            { ruleName: "100%", ruleColor: "#ff0000" },
            { ruleName: "75%", ruleColor: "#ff5500" },
            { ruleName: "50%", ruleColor: "#ff8100" },
            { ruleName: "25%", ruleColor: "#ffea00" },
            { ruleName: "0%", ruleColor: "#00159C" },
        ],
        min: 0,
        max: 1,
        descripcion: "Incidencia de la radiación solar sobre las placas de la cubierta.",
    },
    agua: {
        id: "agua",
        nombre: "Agua caliente",
        corto: "Tuberías",
        unidad: "°C",
        tipo: "sombreado",
        sombreado: { grupo: "First Floor Tubes", sensor: "temperature", radio: 50 },
        colores: [],
        leyenda: [
            { ruleName: "Inferior a 15 °C", ruleColor: "#0E62FF" },
            { ruleName: "15–25 °C", ruleColor: "#4BFF0E" },
            { ruleName: "25–35 °C", ruleColor: "#FFDB0E" },
            { ruleName: "35–45 °C", ruleColor: "#FF630E" },
            { ruleName: "Superior a 45 °C", ruleColor: "#FF3737" },
        ],
        min: 0,
        max: 1,
        descripcion:
            "Temperatura del agua en las tuberías de la planta: la de subida caliente desde la caldera y la de retorno. El modelo se vuelve translúcido para verlas.",
    },
    mantenimiento: {
        id: "mantenimiento",
        nombre: "Mantenimiento fotovoltaico",
        corto: "Mantenim.",
        unidad: "",
        tipo: "elementos",
        colores: [],
        leyenda: ENERGY_MANTENIMIENTO.map((m) => ({ ruleName: m.nombre, ruleColor: m.color })),
        min: 0,
        max: 1,
        descripcion:
            "Estado de cada placa según la inspección termográfica: las que tienen filtraciones, deslaminado o hotspots llevan la fecha de detección.",
    },
    co2: {
        id: "co2",
        tipo: "mapa",
        nombre: "CO₂",
        corto: "CO₂",
        unidad: "ppm",
        colores: [0x1f3fbf, 0x1fb4d8, 0x39c66b, 0xffd500, 0xff8000, 0xe60026],
        leyenda: [
            { ruleName: "Aire exterior (420)", ruleColor: "#1F3FBF" },
            { ruleName: "600 ppm", ruleColor: "#1FB4D8" },
            { ruleName: "800 ppm", ruleColor: "#39C66B" },
            { ruleName: "1.000 ppm", ruleColor: "#FFD500" },
            { ruleName: "1.300 ppm", ruleColor: "#FF8000" },
            { ruleName: "1.500 ppm o más", ruleColor: "#E60026" },
        ],
        min: 400,
        max: 1500,
        descripcion:
            "Concentración de CO₂ en cada zona: sube con la gente y baja cuando la ventilación trabaja. Por encima de 1.000 ppm el aire se nota cargado.",
    },
    temperatura: {
        id: "temperatura",
        tipo: "mapa",
        nombre: "Temperatura",
        corto: "Temp.",
        unidad: "°C",
        colores: [0x1f3fbf, 0x1fb4d8, 0x39c66b, 0xffd500, 0xff8000, 0xe60026],
        leyenda: [
            { ruleName: "16 °C", ruleColor: "#1F3FBF" },
            { ruleName: "19 °C", ruleColor: "#1FB4D8" },
            { ruleName: "22 °C", ruleColor: "#39C66B" },
            { ruleName: "25 °C", ruleColor: "#FFD500" },
            { ruleName: "28 °C", ruleColor: "#FF8000" },
            { ruleName: "31 °C", ruleColor: "#E60026" },
        ],
        min: 16,
        max: 31,
        descripcion:
            "Temperatura de cada zona: la consigna del clima más el calor que aporta la gente; sin clima deriva hacia la temperatura exterior.",
    },
    consumo: {
        id: "consumo",
        tipo: "mapa",
        nombre: "Consumo",
        corto: "Consumo",
        unidad: "W/m²",
        colores: [0x1f3fbf, 0x1fb4d8, 0x39c66b, 0xffd500, 0xff8000, 0xe60026],
        leyenda: [
            { ruleName: "Sin consumo", ruleColor: "#1F3FBF" },
            { ruleName: "12 W/m²", ruleColor: "#1FB4D8" },
            { ruleName: "24 W/m²", ruleColor: "#39C66B" },
            { ruleName: "36 W/m²", ruleColor: "#FFD500" },
            { ruleName: "48 W/m²", ruleColor: "#FF8000" },
            { ruleName: "60 W/m² o más", ruleColor: "#E60026" },
        ],
        min: 0,
        max: 60,
        descripcion:
            "Potencia que consume cada zona ahora mismo por metro cuadrado: clima, iluminación, fuerza de los puestos y cargadores.",
    },
    desperdicio: {
        id: "desperdicio",
        tipo: "mapa",
        nombre: "Desperdicio",
        corto: "Desperdicio",
        unidad: "W/m²",
        colores: [0xa9b8c6, 0xffb000, 0xff8000, 0xe60026],
        leyenda: [
            { ruleName: "Sin desperdicio", ruleColor: "#A9B8C6" },
            { ruleName: "10 W/m²", ruleColor: "#FFB000" },
            { ruleName: "20 W/m²", ruleColor: "#FF8000" },
            { ruleName: "30 W/m² o más", ruleColor: "#E60026" },
        ],
        min: 0,
        max: 30,
        descripcion:
            "Energía que se gasta en zonas donde no hay nadie: clima o luz encendidos por programación horaria, sin detector de presencia.",
    },
};

// ---------------------------------------------------------------------------
// Cifras de referencia
// ---------------------------------------------------------------------------

export const ENERGY_TARIFA = {
    // €/kWh (de los informes del cliente).
    precioKwh: 0.165,
    // kg CO₂e por kWh (alcance 2, de los informes del cliente).
    factorEmision: 0.139,
};

// Umbrales de CO₂ (ppm): exterior, confort y alerta.
export const ENERGY_CO2 = {
    exterior: 420,
    confort: 800,
    cargado: 1000,
    alerta: 1500,
    // Generación de CO₂ por persona (m³/h), actividad de oficina.
    porPersona: 0.0187,
};

// Renovaciones de aire por hora según el estado de la ventilación.
export const ENERGY_VENTILACION = {
    // Sin clima: infiltraciones, puertas y ventilación natural.
    infiltracion: 1.0,
    // Clima en marcha: caudal normal.
    normal: 2.0,
    // Ventilación a demanda: cuando el CO₂ supera `cargado` la máquina sube el
    // caudal (y consume más) hasta que el aire se limpia.
    reforzada: 3.5,
};

// Consigna del clima (°C): calefacción en invierno, refrigeración en verano.
export const ENERGY_CONSIGNA = {
    invierno: 21,
    verano: 24,
    // Meses de verano (0 = enero).
    mesesVerano: [5, 6, 7, 8],
};

// Temperatura exterior media por mes (°C, 0 = enero) y amplitud diaria.
export const ENERGY_EXTERIOR = {
    mediaMensual: [6, 8, 11, 13, 17, 22, 26, 25, 21, 15, 10, 7],
    amplitudDiaria: 4.5,
    // Variación entre días (±), determinista por fecha.
    variacionDiaria: 2.5,
};

// Horarios de PROGRAMACIÓN (laborables). El clima y la luz se encienden por
// horario, no por presencia: de ahí sale el desperdicio cuando no hay nadie.
export const ENERGY_HORARIOS = {
    clima: { inicio: 7.5, fin: 21 },
    luz: { inicio: 7, fin: 21.5 },
    parkingLuz: { inicio: 7, fin: 22 },
};

// Potencias por tipo de zona. W/m² salvo donde se indica.
export interface EnergyZoneParams {
    // Altura libre (m), para el volumen de aire.
    altura: number;
    // Clima a plena carga (W/m²); se modula con la temperatura exterior y con
    // la ocupación (una zona vacía mantiene un mínimo).
    clima: number;
    // Iluminación (W/m²).
    luz: number;
    // Fuerza: por persona presente (W) y en espera por puesto (W).
    fuerzaPorPersona: number;
    fuerzaStandbyPorPuesto: number;
    // Base fija de la zona (W/m²): pantallas, tornos, equipos...
    fuerzaBase: number;
    // Potencia de cargador por plaza ocupada (kW), solo parking eléctrico.
    cargadorKw?: number;
    // CO₂ que genera cada ocupante (m³/h); por defecto ENERGY_CO2.porPersona
    // (sentado en oficina). En el parking el "ocupante" es un coche aparcado.
    co2PorOcupante?: number;
    // Si la luz se enciende por programación aunque no haya nadie.
    luzProgramada: boolean;
    climaProgramado: boolean;
}

const OFICINA: EnergyZoneParams = {
    altura: 2.7,
    clima: 45,
    luz: 9,
    fuerzaPorPersona: 110,
    fuerzaStandbyPorPuesto: 12,
    fuerzaBase: 0,
    luzProgramada: true,
    climaProgramado: true,
};

export const ENERGY_ZONE_PARAMS: { [tipo in AforoZoneType]: EnergyZoneParams } = {
    despacho: { ...OFICINA, clima: 50 },
    sala: { ...OFICINA, clima: 55, luz: 10 },
    trabajo: OFICINA,
    oficina: OFICINA,
    comedor: { ...OFICINA, clima: 40, luz: 10, fuerzaBase: 20, fuerzaPorPersona: 30 },
    hall: {
        altura: 4,
        clima: 35,
        luz: 8,
        fuerzaPorPersona: 0,
        fuerzaStandbyPorPuesto: 0,
        fuerzaBase: 2.5,
        luzProgramada: true,
        climaProgramado: true,
        // Gente de paso, más activa que sentada.
        co2PorOcupante: 0.025,
    },
    acceso: { ...OFICINA, clima: 0, luz: 0, fuerzaPorPersona: 0 },
    recepcion: { ...OFICINA, clima: 0, luz: 0, fuerzaPorPersona: 0 },
    circulacion: {
        altura: 2.7,
        clima: 18,
        luz: 6,
        fuerzaPorPersona: 0,
        fuerzaStandbyPorPuesto: 0,
        fuerzaBase: 0,
        luzProgramada: true,
        climaProgramado: true,
    },
    parking: {
        altura: 2.6,
        clima: 0,
        luz: 3,
        fuerzaPorPersona: 0,
        fuerzaStandbyPorPuesto: 0,
        fuerzaBase: 0,
        luzProgramada: true,
        climaProgramado: false,
        co2PorOcupante: 0.003,
    },
};

// Ajustes por ZONA concreta (id de aforoConfig) que sobrescriben los de su
// tipo: las plazas eléctricas son las únicas con cargador.
export const ENERGY_ZONE_OVERRIDES: { [zoneId: string]: Partial<EnergyZoneParams> } = {
    parkingElectricos: { cargadorKw: 7.4 },
};

// Fracción de la potencia del cargador que consume de media un coche
// enchufado (no todos cargan a tope todo el tiempo: los llenos solo mantienen).
export const ENERGY_CARGADOR_USO = 0.7;

// Superficie que se asume para una zona sin rectángulos calibrados (m²).
export const ENERGY_AREA_POR_DEFECTO_M2 = 60;

// Carga mínima del clima con la zona vacía (fracción de la plena carga) y
// cuánto añade la ocupación completa.
export const ENERGY_CLIMA_CARGA = { minima: 0.45, porOcupacion: 0.55 };

// Suavizado de los valores en vivo (segundos reales para recorrer ~63 % del
// salto): los cambios de hora con el deslizador se ven como una transición.
export const ENERGY_SMOOTHING_SECONDS = 2.5;

// Instalación fotovoltaica de la cubierta (las placas `placa_solar` del
// modelo). Potencia pico, rendimiento del sistema y, por mes, el factor de
// irradiación respecto al mejor mes y las horas de sol (salida y puesta
// aproximadas en hora solar local). La producción diaria lleva además una
// nubosidad determinista por fecha.
export const ENERGY_FV = {
    kwp: 24,
    paneles: 60,
    rendimiento: 0.8,
    factorMensual: [0.5, 0.6, 0.78, 0.88, 0.98, 1.05, 1.1, 1.05, 0.92, 0.75, 0.55, 0.47],
    salida: [8.5, 8.2, 7.6, 7.3, 6.9, 6.8, 7.0, 7.4, 7.8, 8.2, 8.6, 8.7],
    puesta: [18.3, 19.0, 19.5, 20.6, 21.2, 21.6, 21.6, 21.0, 20.2, 19.3, 18.2, 17.9],
    // Producción anual de referencia por kWp (Madrid), para el simulador.
    kwhPorKwpAnio: 1400,
};

// ---------------------------------------------------------------------------
// Colores de las chips de cada zona según el valor (semáforo).
// ---------------------------------------------------------------------------

export const co2ToColor = (ppm: number): string => {
    if (ppm < ENERGY_CO2.confort) return "#00B24A";
    if (ppm < ENERGY_CO2.cargado) return "#FFD500";
    if (ppm < ENERGY_CO2.alerta) return "#FF8000";
    return "#E60026";
};

export const temperaturaToColor = (celsius: number): string => {
    if (celsius >= 20 && celsius <= 24.5) return "#00B24A";
    if (celsius >= 18 && celsius <= 26.5) return "#FFD500";
    return "#E60026";
};

export const consumoToColor = (wPorM2: number): string => {
    if (wPorM2 < 12) return "#00B24A";
    if (wPorM2 < 30) return "#FFD500";
    if (wPorM2 < 45) return "#FF8000";
    return "#E60026";
};

export const desperdicioToColor = (kw: number): string => {
    if (kw <= 0.01) return "#00B24A";
    if (kw < 1) return "#FFB000";
    return "#E60026";
};

// ---------------------------------------------------------------------------
// Formato de cifras (es-ES)
// ---------------------------------------------------------------------------

const es = (value: number, decimals: number): string =>
    value.toLocaleString("es-ES", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });

export const fmtKw = (kw: number): string => (kw < 10 ? `${es(kw, 1)} kW` : `${es(kw, 0)} kW`);

export const fmtKwh = (kwh: number): string =>
    kwh < 10 ? `${es(kwh, 1)} kWh` : `${es(kwh, 0)} kWh`;

export const fmtEur = (eur: number): string => (eur < 100 ? `${es(eur, 2)} €` : `${es(eur, 0)} €`);

export const fmtKg = (kg: number): string => (kg < 10 ? `${es(kg, 1)} kg` : `${es(kg, 0)} kg`);

export const fmtPpm = (ppm: number): string => `${es(Math.round(ppm), 0)} ppm`;

export const fmtGrados = (celsius: number): string => `${es(celsius, 1)} °C`;

export const fmtWm2 = (w: number): string => `${es(w, 0)} W/m²`;

export const fmtPct = (ratio: number): string => `${es(ratio * 100, 0)} %`;
